import { useTranslation } from "react-i18next"

import { BrandLogo } from "@/components/brand-logo"
import { cn } from "@/lib/utils"

/**
 * OKS-383 / OKS-458 — Sticky brand-context strip (UX Spec §2.2).
 *
 * A thin bar pinned to the top of the minimal signup form so the brand
 * anchor ("Joining {Brand}'s crew") stays visible through scroll and
 * field focus — the candidate never loses the "which team am I joining"
 * thread. Replaces the slim logo+name header on `MinimalSignup`; same
 * brand-as-DATA treatment (Crew is NOT white-label).
 *
 * OKS-458 — when `inviterName` is provided, the label becomes a two-line
 * block: line 1 "Joining {Brand}'s crew", line 2 "Invited by {inviter}".
 * Both lines `truncate` so a long brand or inviter name never overflows
 * the strip at the 402px mobile reference (UX Spec §5 edge state). The
 * inviter line is omitted entirely when `inviterName` is null/blank.
 *
 * `sticky top-0` (not fixed) so it participates in layout and never
 * traps focus (UX Spec §6). Brand + inviter surfaced as props — no
 * IDs/slugs, no inviter email (brand isolation P0). `<BrandLogo>` owns
 * the logo→monogram fallback.
 */
export interface BrandContextStripProps {
  brandName: string
  logoUrl: string | null
  /** OKS-458 — inviter display name for the "Invited by {inviter}" line.
   *  Null/omitted → the line is not rendered (back-compat with §01). */
  inviterName?: string | null
  className?: string
  testId?: string
}

export function BrandContextStrip({
  brandName,
  logoUrl,
  inviterName,
  className,
  testId = "brand-context-strip",
}: BrandContextStripProps) {
  const { t } = useTranslation()
  const showInviter = Boolean(inviterName?.trim())

  return (
    <div
      data-testid={testId}
      className={cn(
        "bg-background/95 border-border/40 sticky top-0 z-20 flex items-center gap-2 border-b px-6 py-3 backdrop-blur",
        className,
      )}
    >
      <BrandLogo
        logoUrl={logoUrl}
        name={brandName}
        className="size-6 shrink-0 rounded-md"
        monogramClassName="text-[0.625rem]"
        testId={`${testId}-logo`}
      />
      <div className="flex min-w-0 flex-col">
        <span
          className="text-foreground truncate text-sm font-medium"
          data-testid={`${testId}-label`}
        >
          {t("signup_minimal.context_strip", { brand: brandName })}
        </span>
        {showInviter && (
          <span
            className="text-muted-foreground truncate text-xs"
            data-testid={`${testId}-inviter`}
          >
            {t("signup_minimal.context_strip_inviter", {
              inviter: inviterName,
            })}
          </span>
        )}
      </div>
    </div>
  )
}

export default BrandContextStrip
